import { useContext, useState, useEffect } from "react";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import "./calendarStyle.css";
import { DateRangePicker } from "react-date-range";
import { Box, FormControl, Select, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useFormatDateDisplay } from "../Utils/TimeUtils";
import {
  endOfMonth,
  startOfMonth,
  startOfISOWeek,
  startOfISOWeekYear,
  endOfISOWeek,
  endOfISOWeekYear,
} from "date-fns";
import CustomRangeSelection from "../CustomRangeSelection";
import { GlobalContext } from "../Context";
import { useTranslation } from "react-i18next";

const DateRange = ({ range, setRange, updateType, style, type }) => {
  const { t, i18n } = useTranslation("Stats");
  const { mobileView } = useContext(GlobalContext);
  const formatDateDisplay = useFormatDateDisplay();

  const textStyle = { marginBottom: "0.5rem", fontWeight: 600 };
  const formStyle = {
    width: "100%",
    height: "70%",
    backgroundColor: "white",
    borderRadius: "0.25rem",
  };
  const selectStyle = {
    height: "100%",
    "& .MuiOutlinedInput-notchedOutline": {
      border: "solid 2px #2E117F",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      border: "solid 2px #C31CF3",
    },
    "&:hover": {
      "& .MuiOutlinedInput-notchedOutline": { border: "none" },
    },
  };
  const containerStyle = {
    position: "relative",
    display: "flex",
    flexDirection: mobileView ? "column" : "row",
    width: mobileView ? "100%" : "36rem",
    padding: "2% 3%",
  };
  const closeStyle = {
    position: "absolute",
    top: "0",
    right: "0.5rem",
    color: "white",
    background: "#d9d9d9",
    border: "solid 1px",
    borderRadius: "50%",
    fontSize: "1rem",
  };

  const [isOpen, setIsOpen] = useState(false);
  const [rangeType, setRangeType] = useState("week");

  const updateSelection = (date) => {
    rangeType === "week"
      ? setRange([
          {
            startDate: startOfISOWeek(date),
            key: "selection",
            endDate: endOfISOWeek(date),
          },
        ])
      : rangeType === "month"
      ? setRange([
          {
            startDate: startOfMonth(date),
            key: "selection",
            endDate: endOfMonth(date),
          },
        ])
      : setRange([
          {
            startDate: startOfISOWeekYear(date),
            key: "selection",
            endDate: endOfISOWeekYear(date),
          },
        ]);
  };

  useEffect(() => {
    updateSelection(range[0].startDate);
    updateType(rangeType);
  }, [rangeType]);

  useEffect(() => {
    !type && setRangeType("week");
  }, [type]);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  });

  const handleChange = (ranges) => {
    const { selection } = ranges;
    updateSelection(selection.startDate);
  };

  const handleClose = (event) => {
    const classList = [...event.target.classList];
    const nodeName = event.target.nodeName;

    setIsOpen(
      !classList.includes("MuiBackdrop-root") &&
        !["svg", "path"].includes(nodeName),
    );
  };

  const handleOpen = () => {
    setIsOpen(true);
  };

  const handleScroll = () => {
    setIsOpen(false);
  };

  return (
    <Box sx={style}>
      <Typography sx={textStyle}>{t("progressDateRange")}</Typography>
      <FormControl sx={formStyle}>
        <Select
          sx={selectStyle}
          displayEmpty
          value={range}
          open={isOpen}
          onClose={handleClose}
          onOpen={handleOpen}
          renderValue={() =>
            t("dateRangeFormat", {
              context: rangeType,
              weekRange: formatDateDisplay(range[0]),
              monthRange: range[0].startDate.toLocaleString(i18n.language, {
                month: "long",
              }),
              year: range[0].startDate.getFullYear(),
            })
          }>
          <Box sx={containerStyle}>
            <CloseIcon
              sx={closeStyle}
              onClick={() => {
                setIsOpen(false);
              }}
            />
            <CustomRangeSelection
              selection={rangeType}
              setSelection={setRangeType}
              type={type}
            />
            <DateRangePicker
              onChange={handleChange}
              showSelectionPreview={false}
              months={1}
              maxDate={new Date()}
              ranges={range}
              direction="horizontal"
              className="dateRangePicker"
              onRangeFocusChange={() => {}}
            />
          </Box>
        </Select>
      </FormControl>
    </Box>
  );
};

export default DateRange;
